/*
*  ChangePassword Component
*
* Copyright (C) 2025 Sterilis Solutions, LLC, all rights reserved.
*/
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextField, Button, CircularProgress, Typography, Box } from '@mui/material';
import { toast } from 'react-toastify';
import AuthService from 'src/components/AuthService';
import PasswordField from 'src/components/material/Forms/PasswordField';

const Auth = new AuthService();

const ChangeUserPassword = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordAgain, setNewPasswordAgain] = useState('');
    const [passwordValidationError, setPasswordValidationError] = useState(false);
    const [passwordsNotEqualError, setPasswordsNotEqualError] = useState(false);
    const [passwordFeedback, setPasswordFeedback] = useState('');

    const notifyPasswordSuccess = () => {
        toast.success(t('Successfully changed your password. Please log back in with your new password.'), {
            autoClose: 2000,
            onClose: () => {
                Auth.logout();
                window.location.href = '/login';
            },
        });
    };

    const notifyFailure = (err) => {
        toast.error(t(`Failed to change password. Reason: ${err}`), { autoClose: 5000 });
    };

    const notifyGenericFailure = () => {
        toast.error(t('Failed to complete request. Please refresh this page or try again later.'), { autoClose: 5000 });
    };

    const validatePassword = (password) => {
        if (password.length >= 10) {
            setPasswordValidationError(false);
        } else {
            setPasswordValidationError(true);
        }
    };

    const checkEqualPasswords = (newPassAgain) => {
        if (newPassword === newPassAgain) {
            setPasswordsNotEqualError(false);
        } else {
            setPasswordsNotEqualError(true);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await Auth.fetch('/api/change-password/', {
                method: 'POST',
                body: JSON.stringify({
                    old_password: oldPassword,
                    new_password: newPassword,
                }),
            });

            setIsLoading(false);
            notifyPasswordSuccess();
        } catch (err) {
            setIsLoading(false);
            if (err.response) {
                const resp = await err.response.json();
                if (resp.validation_errors) {
                    setPasswordFeedback(resp.validation_errors.map((fail) => <Typography color="error">{fail}</Typography>));
                } else if (resp.message) {
                    setPasswordFeedback(<Typography color="error">{resp.message}</Typography>);
                    notifyFailure(resp.message);
                } else {
                    notifyGenericFailure();
                }
            } else {
                notifyGenericFailure();
            }
        }
    };

    return (
        <Box component="form" onSubmit={handleChangePassword} id="editPasswordForm" sx={{ mx: 'auto' }}>
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress />
                </Box>
            )}

            <PasswordField
                label={t('Old Password')}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
            />

            <PasswordField
                label={t('New Password')}
                value={newPassword}
                onChange={(e) => {
                    setNewPassword(e.target.value);
                    validatePassword(e.target.value);
                }}
                error={passwordValidationError}
                helperText={passwordValidationError ? t('Passwords must be at least ten (10) characters long') : null}
                required
            />

            <PasswordField
                label={t('New Password Again')}
                value={newPasswordAgain}
                onChange={(e) => {
                    setNewPasswordAgain(e.target.value);
                    checkEqualPasswords(e.target.value);
                }}
                error={passwordsNotEqualError}
                helperText={passwordsNotEqualError ? t('The two new passwords do not match') : null}
                required
            />

            {passwordFeedback && <Box sx={{ mt: 2 }}>{passwordFeedback}</Box>}

            <Button
                type="submit"
                variant="contained"
                color="secondary"
                sx={{ mt: 2 }}
                disabled={passwordValidationError || passwordsNotEqualError || isLoading}
            >
                {t('Change Password')}
            </Button>
        </Box>
    );
};

export default ChangeUserPassword;
